/* Strokes gained engine — pure functions, no DOM.
   strokes gained = expected(start) − expected(end) − strokes used
   Distances: yards off the green (metres converted on the way in), feet on
   the green. Full precision internally; round only for display (fmt).
   Browser: window.WGStrokesGained (needs window.WGSGBaseline loaded first).
   Node: require('./strokes-gained-engine.js') — tests in scripts/tests/. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(require('./strokes-gained-baseline.js'));
  } else {
    root.WGStrokesGained = factory(root.WGSGBaseline);
  }
})(typeof self !== 'undefined' ? self : this, function (BASE) {
  'use strict';

  var METRES_TO_YARDS = 1.09361;
  var COLS = { tee: 1, fairway: 2, rough: 3, sand: 4, recovery: 5 };
  var LIES = ['tee', 'fairway', 'rough', 'sand', 'recovery', 'green', 'holed'];
  var CATEGORIES = ['off_tee', 'approach', 'around_green', 'putting'];
  var CATEGORY_LABELS = {
    off_tee: 'Off the tee',
    approach: 'Approach',
    around_green: 'Around the green',
    putting: 'Putting'
  };
  var AROUND_GREEN_MAX_YARDS = 50;
  var RECOVERY_SHORT_PENALTY = 0.5;

  var LONG_MIN = BASE.long[0][0];
  var LONG_MAX = BASE.long[BASE.long.length - 1][0];
  var PUTT_MIN = BASE.putt[0][0];
  var PUTT_MAX = BASE.putt[BASE.putt.length - 1][0];

  // Linear interpolation over rows [[x, …values]] in column col.
  function interp(rows, col, x) {
    if (x <= rows[0][0]) return rows[0][col];
    for (var i = 1; i < rows.length; i++) {
      if (x <= rows[i][0]) {
        var a = rows[i - 1], b = rows[i];
        var t = (x - a[0]) / (b[0] - a[0]);
        return a[col] + (b[col] - a[col]) * t;
      }
    }
    return rows[rows.length - 1][col];
  }

  function longColumn(lie, yards) {
    // Gap rules for distances the published table does not cover.
    if (lie === 'tee' && yards < 100) return { col: COLS.fairway, add: 0 };
    if (lie === 'recovery' && yards < 100) return { col: COLS.rough, add: RECOVERY_SHORT_PENALTY };
    return { col: COLS[lie], add: 0 };
  }

  /** Expected strokes to hole out. distance: yards (off green) or feet (green). */
  function baseline(lie, distance) {
    if (lie === 'holed') return { value: 0, clamped: false };
    var d = Number(distance);
    if (!isFinite(d) || d <= 0) return null;
    if (lie === 'green') {
      var p = Math.min(Math.max(d, PUTT_MIN), PUTT_MAX);
      return { value: interp(BASE.putt, 1, p), clamped: p !== d };
    }
    if (!(lie in COLS)) return null;
    var y = Math.min(Math.max(d, LONG_MIN), LONG_MAX);
    var c = longColumn(lie, y);
    // Tee values interpolate only across rows that have them (100+ yards).
    var rows = BASE.long;
    if (lie === 'tee' || lie === 'recovery') {
      if (y >= 100) rows = BASE.long.filter(function (r) { return r[c.col] !== null; });
    }
    return { value: interp(rows, c.col, y) + c.add, clamped: y !== d && d > LONG_MIN };
  }

  /** Distance as the engine wants it: yards off the green, feet on it. */
  function toEngineDistance(lie, distance, units) {
    if (lie === 'green' || lie === 'holed') return Number(distance);
    return units === 'm' ? Number(distance) * METRES_TO_YARDS : Number(distance);
  }

  function categorize(startLie, startYards, par) {
    if (startLie === 'green') return 'putting';
    if (startLie === 'tee' && Number(par) >= 4) return 'off_tee';
    if (startYards <= AROUND_GREEN_MAX_YARDS) return 'around_green';
    return 'approach';
  }

  /**
   * shot: { startLie, startDist, endLie, endDist, penalty (bool), units ('yd'|'m'), par }
   * Returns null when the shot is incomplete.
   */
  function strokesGained(shot) {
    if (!shot || LIES.indexOf(shot.startLie) < 0 || shot.startLie === 'holed') return null;
    if (LIES.indexOf(shot.endLie) < 0) return null;
    var units = shot.units || 'yd';
    var sd = toEngineDistance(shot.startLie, shot.startDist, units);
    var ed = shot.endLie === 'holed' ? 0 : toEngineDistance(shot.endLie, shot.endDist, units);
    var s = baseline(shot.startLie, sd);
    var e = baseline(shot.endLie, ed);
    if (!s || !e) return null;
    var strokes = shot.penalty ? 2 : 1;
    return {
      sg: s.value - e.value - strokes,
      startExpected: s.value,
      endExpected: e.value,
      strokes: strokes,
      category: categorize(shot.startLie, sd, shot.par == null ? 4 : shot.par),
      clamped: s.clamped || e.clamped
    };
  }

  /**
   * holes: [{ par, shots: [shot…] }]. A hole is complete when its last shot
   * ends 'holed'. Returns totals, per-category sums, per-hole rows and the
   * best and worst shot.
   */
  function summarizeRound(holes) {
    var byCat = {};
    CATEGORIES.forEach(function (c) { byCat[c] = { sg: 0, count: 0 }; });
    var total = 0, strokesTotal = 0, parTotal = 0, shotsCounted = 0;
    var best = null, worst = null;
    var perHole = (holes || []).map(function (hole, hi) {
      var sgSum = 0, strokes = 0;
      (hole.shots || []).forEach(function (shot, si) {
        var r = strokesGained(Object.assign({}, shot, { par: hole.par }));
        if (!r) return;
        sgSum += r.sg;
        strokes += r.strokes;
        byCat[r.category].sg += r.sg;
        byCat[r.category].count += 1;
        shotsCounted += 1;
        var ref = { hole: hi + 1, shot: si + 1, sg: r.sg, category: r.category };
        if (!best || r.sg > best.sg) best = ref;
        if (!worst || r.sg < worst.sg) worst = ref;
      });
      var shots = hole.shots || [];
      var complete = shots.length > 0 && shots[shots.length - 1].endLie === 'holed';
      if (shots.length) { total += sgSum; strokesTotal += strokes; }
      if (complete) parTotal += Number(hole.par) || 0;
      return { hole: hi + 1, par: hole.par, strokes: strokes, sg: sgSum, shots: shots.length, complete: complete };
    });
    return {
      total: total,
      strokes: strokesTotal,
      parCompleted: parTotal,
      holesComplete: perHole.filter(function (h) { return h.complete; }).length,
      shots: shotsCounted,
      byCategory: byCat,
      perHole: perHole,
      best: best,
      worst: worst
    };
  }

  /** Display: two decimals, always signed, true minus sign, no "-0.00". */
  function fmt(x) {
    var r = Math.round((x + (x >= 0 ? 1e-9 : -1e-9)) * 100) / 100;
    if (Object.is(r, -0) || r === 0) return '+0.00';
    return (r > 0 ? '+' : '−') + Math.abs(r).toFixed(2);
  }

  function readLine(sg) {
    var r = Math.round(sg * 100) / 100;
    if (r >= 0.5) return 'A tour player would take that every day.';
    if (r >= 0.1) return "Better than a tour player's average from there.";
    if (r > -0.1) return 'About what a tour player averages from there.';
    if (r > -0.5) return 'A little behind tour average. Normal for most amateurs.';
    return 'That shot cost you. This is the kind the app flags on the hole map.';
  }

  return {
    baseline: baseline,
    strokesGained: strokesGained,
    categorize: categorize,
    summarizeRound: summarizeRound,
    toEngineDistance: toEngineDistance,
    fmt: fmt,
    readLine: readLine,
    CATEGORIES: CATEGORIES,
    CATEGORY_LABELS: CATEGORY_LABELS,
    LIMITS: { longMin: LONG_MIN, longMax: LONG_MAX, puttMin: PUTT_MIN, puttMax: PUTT_MAX },
    METRES_TO_YARDS: METRES_TO_YARDS
  };
});
